import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import { CategoryScale, Chart, Filler, Legend, LinearScale, LineElement, PointElement, Tooltip } from 'chart.js';
import { Check, X } from 'lucide-react';
import { useConstruction } from '../../context/ConstructionContext';
import { toIsoDate } from '../../construction/evm';
import { formatIndex, formatPct } from '../../construction/money';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import Button from '../common/Button';
import ConstructionShell, { FormError, IndexBadge, Panel, indexTone, inputClass, labelClass, runAction } from './ConstructionShell';
import { itemStatus } from './ConstructionSchedule';
import { t } from '../../i18n';

Chart.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler);

const LOG_TONE = { aprobado: 'success', pendiente: 'warning', rechazado: 'danger' };
const LOG_LABEL = { aprobado: 'Aprobado', pendiente: 'Pendiente', rechazado: 'Rechazado' };

export const SCurveChart = ({ curve, height = 260 }) => {
  if (!curve || curve.length === 0) return <p className="text-xs text-neutral-500">{t('Sin línea base para graficar la curva S.')}</p>;
  const data = {
    labels: curve.map((point) => point.date),
    datasets: [
      { label: t('Programado (línea base)'), data: curve.map((point) => point.planned), borderColor: '#94a3b8', backgroundColor: 'rgba(148,163,184,0.12)', borderDash: [5, 4], pointRadius: 0, tension: 0.25, fill: true },
      { label: t('Real'), data: curve.map((point) => point.actual), borderColor: '#1d5fd1', backgroundColor: '#1d5fd1', pointRadius: 2, tension: 0.25, spanGaps: false }
    ]
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } }, tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y ?? '—'}%` } } },
    scales: { y: { min: 0, max: 100, ticks: { callback: (value) => `${value}%`, font: { size: 11 } } }, x: { ticks: { maxTicksLimit: 8, font: { size: 11 } } } }
  };
  return (
    <div style={{ height }}>
      <Line data={data} options={options} />
    </div>
  );
};

const ProgressContent = ({ summary }) => {
  const { can, registerProgress, reviewProgress, userName } = useConstruction();
  const [form, setForm] = useState({ wbsItemId: '', date: toIsoDate(new Date()), progress: '', note: '' });
  const [error, setError] = useState('');
  const leaves = summary.evm.rows.filter((row) => row.isLeaf);
  const nameById = Object.fromEntries(summary.items.map((item) => [item.id, `${item.code} ${item.name}`]));
  const project = summary.evm.project;
  const canApprove = can('progress.approve');
  const selected = leaves.find((row) => row.id === form.wbsItemId);

  const submit = (event) => {
    event.preventDefault();
    const saved = runAction(() => registerProgress({ ...form, parkId: summary.parkId }), setError);
    if (saved) setForm((prev) => ({ ...prev, progress: '', note: '' }));
  };

  const pending = summary.logs.filter((log) => log.status === 'pendiente');

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi label="% avance construcción" value={formatPct(project.actualProgress)} hint="Ponderado por partida (aprobado)" />
        <Kpi label="% programado a hoy" value={formatPct(project.plannedProgress)} hint="Según línea base" />
        <Kpi label="SPI físico" value={formatIndex(project.progressSpi)} tone={indexTone(project.progressSpi)} hint="Real / programado" />
        <Kpi label="Avances pendientes" value={String(pending.length)} hint="Esperan aprobación del Jefe de Proyecto" />
      </div>

      <Panel title="Curva S" description="Avance acumulado programado (línea base) vs real en el tiempo. Distinto del % avance RTB de Desarrollo.">
        <SCurveChart curve={summary.curve} />
      </Panel>

      {can('progress.register') && (
        <Panel title="Registrar avance" description={canApprove ? 'Como Jefe de Proyecto tu registro queda aprobado.' : 'Tu registro queda pendiente de aprobación del Jefe de Proyecto.'}>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="md:col-span-2">
              <label className={labelClass}>{t('Partida')}</label>
              <select className={inputClass} value={form.wbsItemId} onChange={(event) => setForm((prev) => ({ ...prev, wbsItemId: event.target.value }))}>
                <option value="">{t('Selecciona una partida')}</option>
                {leaves.map((row) => <option key={row.id} value={row.id}>{`${row.code} ${row.name} (${formatPct(row.actual, 0)})`}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>{t('Fecha')}</label>
              <input type="date" className={inputClass} value={form.date} onChange={(event) => setForm((prev) => ({ ...prev, date: event.target.value }))} />
            </div>
            <div>
              <label className={labelClass}>{t('% completado acumulado')}</label>
              <input type="number" min="0" max="100" step="0.5" className={inputClass} placeholder={selected ? String(Math.round(selected.actual)) : ''} value={form.progress} onChange={(event) => setForm((prev) => ({ ...prev, progress: event.target.value }))} />
            </div>
            <Button type="submit" variant="primary" size="sm">{t('Registrar')}</Button>
            <div className="md:col-span-5">
              <input className={inputClass} placeholder={t('Observación (opcional)')} value={form.note} onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))} />
            </div>
          </form>
          <div className="mt-2"><FormError message={error} /></div>
        </Panel>
      )}

      <Panel title="Avance por partida" description="Programado vs real y SPI por partida (real / programado a la fecha).">
        <DataTable
          data={summary.evm.rows.map((row) => ({ ...row, statusLabel: itemStatus(row).label }))}
          columns={[
            { key: 'name', label: 'Partida', render: (value, row) => <span style={{ paddingLeft: row.depth * 14 }} className={row.isLeaf ? '' : 'font-semibold'}><span className="text-neutral-400 mr-1.5">{row.code}</span>{value}</span> },
            { key: 'weightPct', label: 'Ponderación', align: 'right', render: (value) => formatPct(value) },
            { key: 'planned', label: 'Programado', align: 'right', sortable: true, render: (value) => formatPct(value) },
            { key: 'actual', label: 'Real', align: 'right', sortable: true, render: (value) => formatPct(value) },
            { key: 'progressSpi', label: 'SPI', align: 'right', sortable: true, render: (value) => <IndexBadge label="" value={value} /> },
            { key: 'statusLabel', label: 'Estado', render: (value, row) => <Badge tone={itemStatus(row).tone}>{t(value)}</Badge> }
          ]}
          searchPlaceholder="Buscar partida..."
        />
      </Panel>

      <Panel title="Historial de avances" description="Registros de terreno con su estado de aprobación.">
        <DataTable
          data={[...summary.logs].sort((a, b) => (b.date + (b.createdAt || '')).localeCompare(a.date + (a.createdAt || ''))).map((log) => ({
            ...log,
            itemName: nameById[log.wbsItemId] || '—',
            author: userName(log.userId),
            statusLabel: LOG_LABEL[log.status] || log.status
          }))}
          columns={[
            { key: 'date', label: 'Fecha', sortable: true },
            { key: 'itemName', label: 'Partida', sortable: true },
            { key: 'progress', label: '% acumulado', align: 'right', render: (value) => formatPct(value, 1) },
            { key: 'author', label: 'Registrado por' },
            { key: 'note', label: 'Observación', render: (value) => value || '—' },
            { key: 'statusLabel', label: 'Estado', render: (value, row) => <Badge tone={LOG_TONE[row.status]}>{t(value)}</Badge> },
            ...(canApprove
              ? [{
                  key: 'id',
                  label: 'Aprobación',
                  render: (value, row) =>
                    row.status === 'pendiente' ? (
                      <div className="flex gap-1">
                        <Button size="sm" icon={Check} onClick={() => runAction(() => reviewProgress(value, true), setError)}>{t('Aprobar')}</Button>
                        <Button size="sm" variant="danger" icon={X} onClick={() => runAction(() => reviewProgress(value, false), setError)}>{t('Rechazar')}</Button>
                      </div>
                    ) : '—'
                }]
              : [])
          ]}
          searchPlaceholder="Buscar avance..."
          emptyTitle="Sin avances registrados"
          emptyDescription="Registra el primer avance de una partida."
        />
      </Panel>
    </div>
  );
};

export const Kpi = ({ label, value, hint, tone }) => (
  <div className={`bg-white border rounded p-3 ${tone === 'danger' ? 'border-danger-100' : tone === 'warning' ? 'border-warning-100' : 'border-neutral-200'}`}>
    <p className="text-xs font-medium text-neutral-500">{t(label)}</p>
    <p className={`mt-1 text-xl font-semibold num text-left ${tone === 'danger' ? 'text-danger-700' : tone === 'warning' ? 'text-warning-700' : 'text-neutral-900'}`}>{value}</p>
    {hint && <p className="text-[11px] text-neutral-400 mt-0.5">{t(hint)}</p>}
  </div>
);

const ConstructionProgress = () => (
  <ConstructionShell title="Avance físico vs programado" description="Registro de avance por partida, curva S y SPI de la obra." requireItems>
    {(summary) => <ProgressContent summary={summary} />}
  </ConstructionShell>
);

export default ConstructionProgress;
